import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { StatCard } from '@/components/UI/StatCard';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { Tabs } from '@/components/UI/Tabs';
import { Modal } from '@/components/UI/Modal';
import {
  useGetWorkflowDefinitionQuery,
  usePublishWorkflowDefinitionMutation,
  useCreateNewWorkflowVersionMutation,
  useRetireWorkflowDefinitionMutation,
  useDeleteWorkflowDefinitionMutation,
  WorkflowDefinition,
  StageDefinition,
  ProcessDefinition,
  StatusDefinition,
  TransitionDefinition,
  GovernanceGate,
  AutomationHook,
  AuthorityRule,
} from '@/redux/internal-services/workflowApi';

// ─────────────────────────────── Helpers ──────────────────────────────────

type Variant = BadgeProps['variant'];

const STATUS_BADGE: Record<string, Variant> = {
  draft:     'warning',
  published: 'success',
  archived:  'neutral',
};

const LIFECYCLE_BADGE: Record<string, Variant> = {
  entry:        'accent',
  intermediate: 'neutral',
  terminal:     'danger',
};

const OP_BADGE: Record<string, Variant> = {
  normal:    'success',
  on_hold:   'warning',
  closed:    'neutral',
  cancelled: 'warning',
  rejected:  'danger',
};

const TRANSITION_BADGE: Record<string, Variant> = {
  standard:        'accent',
  backward:        'warning',
  skip:            'accent',
  hold:            'warning',
  resume:          'success',
  reopen:          'accent',
  cancel:          'danger',
  reject:          'danger',
  corrective_loop: 'primary',
};

const Tag: React.FC<{ label: string; variant?: Variant }> = ({ label, variant = 'neutral' }) => (
  <Badge variant={variant} className="capitalize">
    {label.replace(/_/g, ' ')}
  </Badge>
);

const RolePill: React.FC<{ role: string }> = ({ role }) => (
  <Badge variant="accent" className="capitalize">
    {role.replace(/_/g, ' ')}
  </Badge>
);

const Section: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({
  title, count, children,
}) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <h3 className="text-sm font-semibold text-ui-text">{title}</h3>
      <Badge variant="neutral">{count}</Badge>
    </div>
    {children}
  </div>
);

const Empty: React.FC<{ label: string }> = ({ label }) => (
  <EmptyState variant="inline" title={label} className="py-4" />
);

// ─────────────────────────────── Tab views ────────────────────────────────

const OverviewTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[
        { label: 'Stages',      value: def.stage_count },
        { label: 'Processes',   value: def.process_count },
        { label: 'Statuses',    value: def.status_count },
        { label: 'Transitions', value: def.transition_count },
        { label: 'Gates',       value: def.gate_count },
        { label: 'Hooks',       value: def.hook_count },
        { label: 'Authority Rules', value: def.authority_rule_count },
        { label: 'Entry Points', value: def.entry_points?.length ?? 0 },
      ].map(({ label, value }) => (
        <StatCard key={label} label={label} value={value} tone="accent" />
      ))}
    </div>

    <div className="card-surface p-5 space-y-3">
      <h3 className="text-sm font-semibold text-ui-text">Definition Info</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <div><dt className="text-ui-text3">ID</dt><dd className="font-mono text-ui-text">{def.definition_id}</dd></div>
        <div><dt className="text-ui-text3">Version</dt><dd className="text-ui-text">v{def.version}</dd></div>
        <div><dt className="text-ui-text3">Status</dt><dd><Tag label={def.status} variant={STATUS_BADGE[def.status]} /></dd></div>
        <div>
          <dt className="text-ui-text3">Engagement Types</dt>
          <dd className="flex flex-wrap gap-1 mt-0.5">
            {def.engagement_types.length === 0
              ? <span className="text-ui-text3">—</span>
              : def.engagement_types.map((et) => <Tag key={et} label={et} />)}
          </dd>
        </div>
        {def.published_at && (
          <div><dt className="text-ui-text3">Published</dt><dd className="text-ui-text">{new Date(def.published_at).toLocaleString()}</dd></div>
        )}
        <div><dt className="text-ui-text3">Created</dt><dd className="text-ui-text">{new Date(def.created_on).toLocaleString()}</dd></div>
        <div><dt className="text-ui-text3">Last Updated</dt><dd className="text-ui-text">{new Date(def.updated_on).toLocaleString()}</dd></div>
      </dl>
      {def.description && (
        <div>
          <p className="text-xs text-ui-text3 mb-1">Description</p>
          <p className="text-sm text-ui-text">{def.description}</p>
        </div>
      )}
    </div>
  </div>
);

const StructureTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => {
  const stageMap = Object.fromEntries(def.stages.map((s) => [s.stage_code, s]));

  return (
    <div className="space-y-8">
      <Section title="Stages" count={def.stages.length}>
        {def.stages.length === 0 ? <Empty label="No stages defined." /> : (
          <div className="space-y-2">
            {def.stages.slice().sort((a, b) => a.display_order - b.display_order).map((stage: StageDefinition) => (
              <div key={stage.stage_code} className="card-surface flex items-center gap-3 p-3">
                <span className="w-7 h-7 rounded-full bg-accent-soft text-accent-text text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {stage.display_order}
                </span>
                <div>
                  <p className="text-sm font-medium text-ui-text">{stage.name}</p>
                  <p className="text-xs font-mono text-ui-text3">{stage.stage_code}</p>
                </div>
                {stage.description && (
                  <p className="text-xs text-ui-text3 ml-auto hidden md:block max-w-xs truncate">{stage.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Processes" count={def.processes.length}>
        {def.processes.length === 0 ? <Empty label="No processes defined." /> : (
          <div className="table-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="th-cell">Process</th>
                    <th className="th-cell">Stage</th>
                    <th className="th-cell hidden md:table-cell">Order</th>
                  </tr>
                </thead>
                <tbody>
                  {def.processes.slice().sort((a, b) => a.display_order - b.display_order).map((p: ProcessDefinition) => (
                    <tr key={p.process_code} className="transition-colors duration-150 hover:bg-ui-hover">
                      <td className="td-cell">
                        <p className="font-medium text-ui-text">{p.name}</p>
                        <p className="text-xs font-mono text-ui-text3">{p.process_code}</p>
                      </td>
                      <td className="td-cell text-ui-text2">{stageMap[p.stage_code]?.name ?? p.stage_code}</td>
                      <td className="td-cell hidden text-ui-text3 md:table-cell">{p.display_order}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      <Section title="Statuses" count={def.statuses.length}>
        {def.statuses.length === 0 ? <Empty label="No statuses defined." /> : (
          <div className="table-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="th-cell">Status</th>
                    <th className="th-cell hidden sm:table-cell">Stage / Process</th>
                    <th className="th-cell">Lifecycle</th>
                    <th className="th-cell hidden md:table-cell">Op Type</th>
                  </tr>
                </thead>
                <tbody>
                  {def.statuses.slice().sort((a, b) => a.display_order - b.display_order).map((s: StatusDefinition) => (
                    <tr key={s.status_code} className="transition-colors duration-150 hover:bg-ui-hover">
                      <td className="td-cell">
                        <p className="font-medium text-ui-text">{s.name}</p>
                        <p className="text-xs font-mono text-ui-text3">{s.status_code}</p>
                      </td>
                      <td className="td-cell hidden text-ui-text2 sm:table-cell">
                        <p className="text-xs">{stageMap[s.process_code]?.name ?? s.process_code}</p>
                        <p className="text-xs text-ui-text3">{s.process_code}</p>
                      </td>
                      <td className="td-cell">
                        <Tag label={s.lifecycle_role} variant={LIFECYCLE_BADGE[s.lifecycle_role]} />
                      </td>
                      <td className="td-cell hidden md:table-cell">
                        <Tag label={s.operational_type} variant={OP_BADGE[s.operational_type]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      <Section title="Entry Points" count={def.entry_points?.length ?? 0}>
        {!def.entry_points?.length ? <Empty label="No entry points defined." /> : (
          <div className="space-y-2">
            {def.entry_points.map((ep) => (
              <div key={ep.status_code} className="card-surface flex items-start gap-3 p-3 text-sm">
                <span className="font-mono text-ui-text font-medium">{ep.status_code}</span>
                <div className="flex flex-wrap gap-1">
                  {ep.engagement_types.map((et) => (
                    <Tag key={et} label={et} />
                  ))}
                </div>
                {ep.description && (
                  <span className="text-ui-text3 text-xs ml-auto hidden md:block">{ep.description}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

const TransitionsTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => {
  const statusMap = Object.fromEntries(def.statuses.map((s) => [s.status_code, s.name]));

  return (
    <Section title="Transitions" count={def.transitions.length}>
      {def.transitions.length === 0 ? <Empty label="No transitions defined." /> : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th-cell">Transition</th>
                  <th className="th-cell">From → To</th>
                  <th className="th-cell">Category</th>
                  <th className="th-cell hidden lg:table-cell">Reason</th>
                </tr>
              </thead>
              <tbody>
                {def.transitions.map((t: TransitionDefinition) => (
                  <tr key={t.transition_code} className="transition-colors duration-150 hover:bg-ui-hover">
                    <td className="td-cell">
                      <p className="font-medium text-ui-text">{t.action_label}</p>
                      <p className="text-xs font-mono text-ui-text3">{t.transition_code}</p>
                      {t.description && <p className="text-xs text-ui-text3 mt-0.5">{t.description}</p>}
                    </td>
                    <td className="td-cell">
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-ui-text">{statusMap[t.source_status_code] ?? t.source_status_code}</span>
                        <span className="text-ui-text3">→</span>
                        <span className="text-ui-text">{statusMap[t.target_status_code] ?? t.target_status_code}</span>
                      </div>
                      <div className="flex gap-1 mt-0.5">
                        <span className="font-mono text-ui-text3 text-xs">{t.source_status_code}</span>
                        <span className="text-ui-text3 text-xs">→</span>
                        <span className="font-mono text-ui-text3 text-xs">{t.target_status_code}</span>
                      </div>
                    </td>
                    <td className="td-cell">
                      <Tag label={t.category} variant={TRANSITION_BADGE[t.category] ?? 'neutral'} />
                    </td>
                    <td className="td-cell hidden lg:table-cell">
                      {t.requires_reason
                        ? <span className="text-xs text-warning-text">Required</span>
                        : <span className="text-xs text-ui-text3">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Section>
  );
};

const GatesTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <Section title="Governance Gates" count={def.governance_gates.length}>
    {def.governance_gates.length === 0 ? <Empty label="No governance gates defined." /> : (
      <div className="space-y-3">
        {def.governance_gates.map((gate: GovernanceGate) => (
          <div key={gate.gate_code} className="card-surface p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-ui-text font-mono">{gate.gate_code}</p>
                <p className="text-xs text-ui-text3 mt-0.5">{gate.failure_message}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Tag label={gate.scope} />
                <Tag
                  label={gate.classification}
                  variant={gate.classification === 'blocking' ? 'danger' : 'warning'}
                />
              </div>
            </div>
            {gate.description && <p className="text-xs text-ui-text3 mb-2">{gate.description}</p>}
            <div className="flex flex-wrap items-center gap-3 text-xs text-ui-text3 mb-2">
              <span>Target: <span className="font-mono text-ui-text">{gate.target_ref}</span></span>
              <span>Condition: <span className="text-ui-text">{gate.condition_type}</span></span>
            </div>
            {gate.condition_config && Object.keys(gate.condition_config).length > 0 && (
              <div>
                <p className="text-xs font-medium text-ui-text2 mb-1">Config:</p>
                <pre className="text-xs text-ui-text3 bg-ui-subtle rounded-chip p-2 overflow-x-auto">
                  {JSON.stringify(gate.condition_config, null, 2)}
                </pre>
              </div>
            )}
            {gate.required_next_action && (
              <p className="text-xs text-ui-text2 mt-2">
                <span className="font-medium">Next action: </span>{gate.required_next_action}
              </p>
            )}
          </div>
        ))}
      </div>
    )}
  </Section>
);

const HooksTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <Section title="Automation Hooks" count={def.automation_hooks.length}>
    {def.automation_hooks.length === 0 ? <Empty label="No automation hooks defined." /> : (
      <div className="space-y-3">
        {def.automation_hooks.map((hook: AutomationHook) => (
          <div key={hook.hook_code} className="card-surface p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-ui-text font-mono">{hook.hook_code}</p>
                {hook.description && <p className="text-xs text-ui-text3 mt-0.5">{hook.description}</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                <Tag label={hook.trigger_point} variant="accent" />
                <Tag label={hook.action_type.replace(/_/g, ' ')} variant="accent" />
                {hook.is_mandatory && (
                  <Tag label="mandatory" variant="danger" />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-ui-text3">
              <span>Trigger ref:</span>
              <span className="font-mono text-ui-text">{hook.trigger_ref}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </Section>
);

const AuthorityTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <Section title="Authority Rules" count={def.authority_rules.length}>
    {def.authority_rules.length === 0 ? <Empty label="No authority rules defined." /> : (
      <div className="table-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th-cell">Rule</th>
                <th className="th-cell">Authority Type</th>
                <th className="th-cell hidden sm:table-cell">Target Refs</th>
                <th className="th-cell">Allowed Roles</th>
                <th className="th-cell hidden md:table-cell">Assignment</th>
              </tr>
            </thead>
            <tbody>
              {def.authority_rules.map((rule: AuthorityRule) => (
                <tr key={rule.rule_code} className="transition-colors duration-150 hover:bg-ui-hover">
                  <td className="td-cell">
                    <p className="font-mono text-xs text-ui-text">{rule.rule_code}</p>
                    {rule.description && <p className="text-xs text-ui-text3 mt-0.5">{rule.description}</p>}
                  </td>
                  <td className="td-cell">
                    <Tag label={rule.authority_type} variant="accent" />
                  </td>
                  <td className="td-cell hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {rule.target_refs.map((ref) => (
                        <span key={ref} className="font-mono text-xs px-1.5 py-0.5 rounded-chip bg-ui-subtle text-ui-text2">{ref}</span>
                      ))}
                    </div>
                  </td>
                  <td className="td-cell">
                    <div className="flex flex-wrap gap-1">
                      {rule.allowed_roles.map((r) => <RolePill key={r} role={r} />)}
                    </div>
                  </td>
                  <td className="td-cell hidden md:table-cell">
                    <Tag label={rule.assignment_scope ?? 'any'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </Section>
);

// ─────────────────────────────── Main page ────────────────────────────────

type Tab = 'overview' | 'structure' | 'transitions' | 'gates' | 'hooks' | 'authority';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview',    label: 'Overview' },
  { id: 'structure',   label: 'Structure' },
  { id: 'transitions', label: 'Transitions' },
  { id: 'gates',       label: 'Governance Gates' },
  { id: 'hooks',       label: 'Automation Hooks' },
  { id: 'authority',   label: 'Authority Rules' },
];

const WorkflowDefinitionPage: React.FC = () => {
  const { t } = useTranslation();
  const { definitionId = '', version = '1' } = useParams<{ definitionId: string; version: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [confirmAction, setConfirmAction] = useState<'publish' | 'retire' | 'delete' | null>(null);

  const { data, isLoading } = useGetWorkflowDefinitionQuery(
    { definition_id: definitionId, version: Number(version) },
    { skip: !definitionId },
  );

  const [publish, { isLoading: publishing }] = usePublishWorkflowDefinitionMutation();
  const [createNewVersion, { isLoading: revising }] = useCreateNewWorkflowVersionMutation();
  const [retire,  { isLoading: retiring  }] = useRetireWorkflowDefinitionMutation();
  const [remove,  { isLoading: deleting  }] = useDeleteWorkflowDefinitionMutation();
  const [revisionError, setRevisionError] = useState('');

  const def = data?.data;

  const handlePublish = async () => {
    await publish({ definition_id: definitionId, version: Number(version) });
    setConfirmAction(null);
  };

  const handleCreateNewVersion = async () => {
    setRevisionError('');
    try {
      const result = await createNewVersion({ definition_id: definitionId, version: Number(version) }).unwrap();
      navigate(`/platform/workflows/${result.data.definition_id}/${result.data.version}/edit`);
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setRevisionError(err?.data?.message ?? 'Failed to create a new version.');
    }
  };

  const handleRetire = async () => {
    await retire({ definition_id: definitionId, version: Number(version) });
    setConfirmAction(null);
  };

  const handleDelete = async () => {
    await remove({ definition_id: definitionId, version: Number(version) });
    setConfirmAction(null);
    navigate('/platform/workflows');
  };

  if (isLoading) {
    return (
      <PageShell header={null}>
        <LoadingState label="Loading workflow definition..." className="py-20" />
      </PageShell>
    );
  }

  if (!def) {
    return (
      <PageShell header={null}>
        <EmptyState
          title={t('platform.workflowDefinitionNotFound')}
          className="py-20"
          action={
            <Button variant="link" size="sm" className="text-accent-text" onClick={() => navigate('/platform/workflows')}>
              ← Back to Workflows
            </Button>
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          back={
            <Button variant="link" size="xs" onClick={() => navigate('/platform/workflows')}>
              ← Workflows
            </Button>
          }
          title={
            <span className="flex flex-wrap items-center gap-3">
              {def.name}
              <Badge variant={STATUS_BADGE[def.status]} className="font-semibold capitalize">
                {def.status}
              </Badge>
            </span>
          }
          subtitle={
            <>
              <span className="font-mono text-ui-text3">{def.definition_id} · v{def.version}</span>
              {def.description && <span className="mt-1 block">{def.description}</span>}
            </>
          }
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/platform/workflows/${def.definition_id}/${def.version}/flowchart`)}
              >
                View Flowchart
              </Button>
              {def.status === 'draft' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/platform/workflows/${def.definition_id}/${def.version}/edit`)}
                  >
                    Edit
                  </Button>
                  <Button size="sm" onClick={() => setConfirmAction('publish')}>
                    Publish
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmAction('delete')}>
                    Delete Draft
                  </Button>
                </>
              )}
              {def.status === 'published' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleCreateNewVersion()}
                    disabled={revising}
                  >
                    {revising ? 'Creating…' : 'Create New Version'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmAction('retire')}>
                    Retire
                  </Button>
                </>
              )}
            </div>
          }
          toolbar={revisionError ? <p className="text-xs text-danger-text">{revisionError}</p> : undefined}
        />
      }
    >
      {/* Tabs */}
      <Tabs
        className="mb-6"
        label="Workflow definition sections"
        tabs={TABS}
        value={activeTab}
        onChange={(id) => setActiveTab(id as Tab)}
      />

      {/* Tab content */}
      {activeTab === 'overview'    && <OverviewTab    def={def} />}
      {activeTab === 'structure'   && <StructureTab   def={def} />}
      {activeTab === 'transitions' && <TransitionsTab def={def} />}
      {activeTab === 'gates'       && <GatesTab       def={def} />}
      {activeTab === 'hooks'       && <HooksTab       def={def} />}
      {activeTab === 'authority'   && <AuthorityTab   def={def} />}

      {/* Confirm modal */}
      {confirmAction && (
        <Modal
          title={confirmAction === 'delete' ? 'Delete Draft' : `${confirmAction.charAt(0).toUpperCase()}${confirmAction.slice(1)} Workflow`}
          onClose={() => setConfirmAction(null)}
          onSave={confirmAction === 'publish' ? handlePublish : confirmAction === 'retire' ? handleRetire : handleDelete}
          saving={publishing || retiring || deleting}
          saveLabel={`Confirm ${confirmAction}`}
          danger={confirmAction === 'delete' || confirmAction === 'retire'}
          maxWidthClassName="max-w-sm"
        >
          <p className="text-sm text-ui-text2">
            {confirmAction === 'publish' && 'Publishing makes this workflow available to tenants. This action cannot be undone to draft.'}
            {confirmAction === 'retire'  && 'Retiring archives this workflow. Active engagements continue; no new engagements can adopt it.'}
            {confirmAction === 'delete'  && 'This will permanently delete this draft definition.'}
          </p>
        </Modal>
      )}
    </PageShell>
  );
};

export default WorkflowDefinitionPage;
